import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DbNewsItem } from "../../../lib/supabase";
import { fetchNewsItem, fetchRelatedNews, getLang } from "../../../lib/newsUtils";

const CATEGORY_STYLES: Record<string, string> = {
  "grant-call": "bg-gold/10 text-gold-dark",
  news: "bg-navy/10 text-navy",
  event: "bg-emerald-50 text-emerald-700",
  result: "bg-cool-gray-100 text-navy/60",
};

export default function ArticleView() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [article, setArticle] = useState<DbNewsItem | null>(null);
  const [related, setRelated] = useState<DbNewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const lang = getLang(i18n.language);
      const data = await fetchNewsItem(slug ?? "", lang);
      if (data) {
        setArticle(data);
        const rel = await fetchRelatedNews(data.category, slug ?? "", lang);
        setRelated(rel);
      } else {
        setArticle(null);
      }
      setLoading(false);
    };
    load();
  }, [slug, i18n.language]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  const formatDate = (d: string) => {
    const locale = i18n.language.startsWith("ro")
      ? "ro-RO"
      : i18n.language.startsWith("ru")
      ? "ru-RU"
      : "en-GB";
    return new Date(d).toLocaleDateString(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <main className="pt-16 min-h-screen bg-cool-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 flex items-center justify-center">
          <i className="ri-loader-4-line text-3xl text-navy/30 animate-spin" />
        </div>
      </main>
    );
  }

  if (!article) {
    return (
      <main className="pt-16 min-h-screen bg-cool-gray-50 flex items-center justify-center">
        <div className="text-center px-6">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <i className="ri-file-search-line text-5xl text-navy/20" />
          </div>
          <h1 className="text-2xl font-bold text-navy mb-3">{t("article.notFound.title")}</h1>
          <p className="text-navy/50 mb-6 text-sm">{t("article.notFound.p")}</p>
          <Link
            to="/news"
            className="inline-flex items-center gap-2 px-6 py-3 bg-navy text-white text-sm font-semibold rounded-md hover:bg-navy/90 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            {t("article.notFound.back")}
          </Link>
        </div>
      </main>
    );
  }

  const isHtml = /<[a-z][\s\S]*>/i.test(article.body);

  const bodyParagraphs = isHtml
    ? []
    : article.body
        .split(/\.(?:\s+)/)
        .reduce((acc: string[], sentence, i, arr) => {
          const idx = Math.floor(i / 3);
          if (!acc[idx]) acc[idx] = "";
          acc[idx] += sentence + (i < arr.length - 1 ? ". " : "");
          return acc;
        }, [])
        .filter(Boolean);

  return (
    <main className="pt-16">
      {/* Hero image */}
      <div className="relative w-full h-[55vh] md:h-[65vh] overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/30 to-transparent" />

        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-6 left-6 md:left-12 inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm text-white text-xs font-semibold rounded-md border border-white/20 hover:bg-white/25 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" />
          {t("article.back")}
        </button>

        {/* Category badge */}
        <div className="absolute top-6 right-6 md:right-12 flex items-center gap-2">
          <span className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider bg-gold text-white rounded-full">
            {article.category_label}
          </span>
        </div>

        {/* Title overlay */}
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 lg:px-24 pb-10">
          <div className="max-w-4xl">
            {article.deadline && (
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-alarm-line text-red-400 text-sm" />
                <span className="text-red-300 text-xs font-semibold uppercase tracking-wider">
                  {t("article.deadline")} {formatDate(article.deadline)}
                </span>
              </div>
            )}
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
              {article.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Article body */}
      <section className="bg-white py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-14">
            {/* Main content */}
            <div className="lg:col-span-2">
              {/* Meta row */}
              <div className="flex flex-wrap items-center gap-4 mb-8 pb-6 border-b border-cool-gray-200">
                <span className={`text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full ${CATEGORY_STYLES[article.category]}`}>
                  {article.category_label}
                </span>
                <span className="text-xs text-navy/40 flex items-center gap-1.5">
                  <i className="ri-calendar-line" />
                  {formatDate(article.date)}
                </span>
                {article.source && (
                  <span className="text-xs text-navy/40 flex items-center gap-1.5">
                    <i className="ri-building-line" />
                    {article.source}
                  </span>
                )}
              </div>

              {/* Lead paragraph */}
              <p className="text-lg md:text-xl text-navy/80 leading-relaxed font-light mb-8 border-l-2 border-gold pl-5">
                {article.excerpt}
              </p>

              {/* Body */}
              <div className="space-y-5">
                {isHtml ? (
                  <div
                    className="article-body"
                    dangerouslySetInnerHTML={{ __html: article.body }}
                  />
                ) : (
                  bodyParagraphs.map((para, i) => (
                    <p key={i} className="text-[15px] text-navy/70 leading-relaxed">
                      {para}
                    </p>
                  ))
                )}
              </div>

              {/* Tags */}
              {article.tags && article.tags.length > 0 && (
                <div className="mt-10 pt-8 border-t border-cool-gray-200">
                  <p className="text-xs uppercase tracking-widest text-navy/40 font-medium mb-4">
                    {t("article.tags")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1.5 text-xs font-medium text-navy/60 bg-cool-gray-100 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA */}
              {article.category === "grant-call" && (
                <div className="mt-10 p-6 bg-navy rounded-lg">
                  <p className="text-sm font-semibold text-white mb-2">
                    {t("article.cta.title")}
                  </p>
                  <p className="text-sm text-white/60 mb-4">
                    {t("article.cta.p")}
                  </p>
                  <Link
                    to="/#contact"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold text-white text-xs font-semibold rounded-md hover:bg-gold-light transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {t("article.cta.btn")}
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Key facts */}
              <div className="bg-cool-gray-50 rounded-lg p-6 mb-8">
                <p className="text-xs uppercase tracking-widest text-gold font-medium mb-5">
                  {t("article.sidebar.info")}
                </p>
                <ul className="space-y-4">
                  {article.deadline && (
                    <li className="flex items-start gap-3">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-time-line text-gold" />
                      </span>
                      <div>
                        <p className="text-xs text-navy/40 uppercase tracking-wider font-medium mb-0.5">
                          {t("article.sidebar.deadline")}
                        </p>
                        <p className="text-sm font-semibold text-red-600">{formatDate(article.deadline)}</p>
                      </div>
                    </li>
                  )}
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-calendar-check-line text-gold" />
                    </span>
                    <div>
                      <p className="text-xs text-navy/40 uppercase tracking-wider font-medium mb-0.5">
                        {t("article.sidebar.published")}
                      </p>
                      <p className="text-sm text-navy">{formatDate(article.date)}</p>
                    </div>
                  </li>
                  {article.source && (
                    <li className="flex items-start gap-3">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-building-2-line text-gold" />
                      </span>
                      <div>
                        <p className="text-xs text-navy/40 uppercase tracking-wider font-medium mb-0.5">
                          {t("article.sidebar.source")}
                        </p>
                        <p className="text-sm text-navy">{article.source}</p>
                      </div>
                    </li>
                  )}
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-price-tag-3-line text-gold" />
                    </span>
                    <div>
                      <p className="text-xs text-navy/40 uppercase tracking-wider font-medium mb-0.5">
                        {t("article.sidebar.category")}
                      </p>
                      <p className="text-sm text-navy">{article.category_label}</p>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Related articles */}
              {related.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-navy/40 font-medium mb-5">
                    {t("article.related")}
                  </p>
                  <ul className="space-y-5">
                    {related.map((item) => (
                      <li key={item.id}>
                        <Link
                          to={`/news/${item.slug}`}
                          className="group flex gap-4 cursor-pointer"
                        >
                          <div className="w-16 h-16 flex-shrink-0 overflow-hidden rounded-md">
                            <img
                              src={item.image}
                              alt={item.title}
                              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-navy/80 leading-snug group-hover:text-gold transition-colors line-clamp-3">
                              {item.title}
                            </p>
                            <p className="text-xs text-navy/35 mt-1">{formatDate(item.date)}</p>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-cool-gray-200">
                <Link
                  to="/news"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-gold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line" />
                  {t("article.allNews")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
